// web/src/api/assist/actions-collect.ts —— 坐席一键动作（意图联动）+ 坐席收号与数据源动作
// 拆自 web/src/api/assist.ts 的同名分区（内容原样搬运）；由 web/src/api/assist.ts 以 export * 聚合，导出面对调用方不变。

import { del, get, post, put, type ReqConfig } from '../../request';
// ---------- 坐席一键动作（意图联动） ----------

export interface ExecuteActionParams {
  action: 'transfer' | 'create_ticket' | 'satisfaction' | 'confirm_solve' | 'query' | 'broadcast';
  uuid: string;
  /** 转接目标队列键（action=transfer 且来电尚未接听时用）：后端按它把客户腿转入该技能组重新排队 */
  queue?: string;
  /**
   * 转指定分机（action=transfer 且来电尚未接听时用，与 queue 二选一、分机优先）：
   * 后端解析该分机的坐席与其技能组并入队，条目只振铃该分机；对方不可接听时按 400 返回原因。
   */
  target_extension?: string;
  reason?: string;
  /** v5 建单来源意图 code（SourceRef 幂等维度） */
  intent_code?: string;
  /** 手动补查/重查的查询动作 code（action=query 时必填） */
  api_code?: string;
  /** 播报文案（action=broadcast 时必填）：坐席确认后 TTS 朗读给客户 */
  text?: string;
  /** v5 工单模板 id（采集字段/默认值/流程绑定） */
  ticket_template_id?: number;
  /** v?.x 数据源查询参数（值可为 {slot.<key>} 占位，后端从 session 插槽提取明文） */
  params?: Record<string, any>;
  ticket?: { title?: string; type?: string; priority?: string; content?: string };
}

/** 坐席一键执行：转人工 / 建工单 / 满意度 / 确认解决 / 数据源查询（POST /assist/actions/execute） */
export function executeAssistAction(p: ExecuteActionParams): Promise<{ ok: boolean; action: string; ticket_id?: number; detail?: string; result?: any; success?: boolean; text?: string; error?: string }> {
  return post('/assist/actions/execute', p);
}

// ---------- 坐席收号 + 数据源动作（v?.x） ----------

/** 收号字段目录条目（GET /assist/collect-fields，坐席收号下拉） */
export interface CollectFieldItem {
  id?: number;
  key: string;
  label: string;
  mode: 'phone' | 'id_tail' | 'id_card' | 'number' | 'text';
  secure?: boolean;
  slot?: string;
  api_key?: string;
  prompt?: string;
  enabled?: boolean;
  sort?: number;
}

/** 坐席收号字段目录（启用中） */
export function listCollectFields(): Promise<CollectFieldItem[]> {
  return get('/assist/collect-fields');
}

/**
 * 坐席主动收号：manual_value 空=DTMF 采集；非空=手动录入直接落地 session 插槽。
 * 传 `{silent:true}` 供后台自动写入（本通号码回填收号）使用：服务端归属校验未通过时返回
 * 404 call not found，属「该 uuid 尚未归属本人」的正常中间态，不该以原始英文提示打扰坐席。
 */
export function requestCollect(
  p: { uuid: string; field_key: string; manual_value?: string },
  cfg?: ReqConfig
): Promise<{ ok: boolean }> {
  return post('/assist/collect/request', p, cfg);
}

/** session 插槽列表（值脱敏，供数据源动作参数预填 + {slot.<key>} 占位选择；无插槽/无权 404 静默） */
export function sessionSlots(uuid: string): Promise<Array<{ key: string; label: string; mode: string; slot: string; api_key: string; value: string }>> {
  return get('/assist/session-slots', { uuid }, { silent: true });
}

/** 数据源动作参数 schema（含 pii） */
export interface DataApiParamSpec {
  key: string;
  label: string;
  type?: string;
  enum?: string[];
  required?: boolean;
  source?: string;
  prompt?: string;
  max_tries?: number;
  value?: string;
  pii?: boolean;
}

/** 数据源动作（数据源内的查询方法；坐席「功能调用」动作下拉） */
export interface DataApiOption {
  code: string;
  name: string;
  parameters?: DataApiParamSpec[];
}

/** 坐席可访问的数据源（含其启用查询动作，v?.x 两级下拉：①数据源 → ②动作，均显示名称） */
export interface DataSourceOption {
  id: number;
  name: string;
  apis: DataApiOption[];
}

/** 坐席可访问数据源/动作（启用中，按数据源分组，含参数 schema） */
export function listDataAPIs(): Promise<DataSourceOption[]> {
  return get('/assist/data-apis');
}

/** 坐席收号清除/换号重采：field_key 空=清空该通话全部收号（快照槽位 + 会话插槽 + 采集行） */
export function clearCollect(p: { uuid: string; field_key?: string }): Promise<{ ok: boolean }> {
  return post('/assist/collect/clear', p);
}

/** 已发布启用流程清单（坐席「自主选择流程」下拉，GET /assist/flows） */
export interface PublishedFlowOption {
  id: number;
  code: string;
  name: string;
  channel_scope?: string;
}
export function listFlows(): Promise<PublishedFlowOption[]> {
  return get('/assist/flows');
}

/** 在线会话 session 插槽列表（值脱敏，GET /assist/session-slots?session_id=） */
export function chatSessionSlots(sessionId: number): Promise<Array<{ key: string; label: string; mode: string; slot: string; api_key: string; value: string }>> {
  return get('/assist/session-slots', { session_id: sessionId }, { silent: true });
}

/** 在线会话坐席数据源查询（POST /assist/chat-query）：默认 phone=会话号码，{slot.} 从会话插槽解析 */
export function chatQuery(p: { session_id: number; api_code: string; params?: Record<string, any> }): Promise<{ ok: boolean; action: string; success?: boolean; cost_ms?: number; result?: any; text?: string; status?: string; error?: string }> {
  return post('/assist/chat-query', p);
}

